<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="store.deleteDialogOpen"
        class="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-[60] flex items-center justify-center px-4"
        @click="store.closeDeleteDialog()"
      >
        <div
          class="w-full max-w-sm bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl p-5"
          @click.stop
        >
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">
            {{ isSeries ? 'Delete recurring series' : isRecurring ? 'Delete recurring task' : 'Delete task' }}
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {{ task?.title }}
          </p>

          <div v-if="isRecurring && !isSeries" class="space-y-2 mb-5">
            <label v-for="opt in scopeOptions" :key="opt.value" class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="radio" :value="opt.value" v-model="scope" class="mt-0.5 text-blue-600 focus:ring-blue-500" />
              <span>{{ opt.label }}</span>
            </label>
          </div>
          <p v-else class="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {{ isSeries ? 'This deletes every past and future occurrence. This cannot be undone.' : 'This cannot be undone.' }}
          </p>

          <div class="flex justify-end gap-2">
            <button
              @click="store.closeDeleteDialog()"
              class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >Cancel</button>
            <button
              @click="confirm"
              :disabled="deleting"
              class="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
            >{{ deleting ? 'Deleting...' : 'Delete' }}</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTasksStore } from 'stores/tasks.js'

const store = useTasksStore()
const router = useRouter()
const deleting = ref(false)
const scope = ref('only')

const task = computed(() => store.deleteDialogTask)
const isRecurring = computed(() => !!task.value?.is_recurring)
const isSeries = computed(() => task.value?.task_type === 'recurring_series')

const scopeOptions = [
  { value: 'only', label: 'This task only' },
  { value: 'following', label: 'This and following tasks' },
  { value: 'all', label: 'All tasks in the series' },
]

watch(() => store.deleteDialogOpen, (open) => { if (open) scope.value = 'only' })

async function confirm() {
  if (!task.value) return
  deleting.value = true
  try {
    const ok = await store.deleteTask(task.value, isSeries.value ? 'all' : isRecurring.value ? scope.value : 'only')
    if (ok) {
      const redirectTo = store.deleteDialogRedirectTo
      store.closeDeleteDialog()
      if (redirectTo) router.push(redirectTo)
    }
  } finally {
    deleting.value = false
  }
}
</script>

<style scoped>
.fade-enter-active { transition: opacity 0.15s ease; }
.fade-leave-active { transition: opacity 0.12s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
